import './caseStudy.css'
import Footer from "./footer"
import RevealTitle from "./RevealTitle"

const ENDPOINTS = [
    {
        path: "/extract/portrait",
        steps: ["OpenCV grayscale", "Haar cascade detect", "Crop & base64 encode"],
        b: "Finds the face on the document and returns the cropped portrait as a base64 string.",
    },
    {
        path: "/extract/fields",
        steps: ["EasyOCR text detect", "Regex pattern match", "Key-value extraction"],
        b: "Reads the document text and pulls out Name, Date of Birth, and Expiry.",
    },
    {
        path: "/extract/mrz",
        steps: ["EasyOCR character scan", "MRZ logic parser"],
        b: "Locates the machine-readable zone and parses it into structured fields.",
    },
];

const STACK = [
    { title: "Core", items: ["Python", "FastAPI"] },
    { title: "Vision & OCR", items: ["OpenCV", "Haar Cascades", "EasyOCR", "PyTorch"] },
    { title: "Ops", items: ["Docker", "Docker Hub", "Git"] },
];

export default function Photoextr() {
    return (
        <>
            <div className="cs-wrap">
                <header className="cs-hero">
                    <p className="cs-eyebrow">Case study · AI · Etisalat</p>
                    <RevealTitle text="ID Document Intelligence" className="cs-title" />
                    <p className="cs-subtitle">
                        An API that extracts the portrait, key fields, and MRZ from any ID document or passport — computer vision + OCR for KYC / KYB automation.
                    </p>
                </header>

                <div className="cs-links">
                    <a className="cs-linkBtn" href="https://github.com/mohammed0Swalih/photoDetectionAndOCRExtr" target="_blank" rel="noreferrer">GitHub ↗</a>
                    <a className="cs-linkBtn" href="https://hub.docker.com/r/2023bcs0193swalih/photoextr" target="_blank" rel="noreferrer">Docker Hub ↗</a>
                </div>

                <div className="cs-cover">
                    <div className="cs-ph cs-ph--cover">UI / API demo — screenshot (coming)</div>
                </div>

                <div className="cs-meta">
                    <div><p className="cs-meta-label">Role</p><p className="cs-meta-value">Computer vision &amp; OCR (solo)</p></div>
                    <div><p className="cs-meta-label">Context</p><p className="cs-meta-value">Etisalat internship</p></div>
                    <div><p className="cs-meta-label">Type</p><p className="cs-meta-value">KYC / KYB API service</p></div>
                    <div><p className="cs-meta-label">Status</p><p className="cs-meta-value">Dockerized · on Docker Hub</p></div>
                </div>

                {/* Overview */}
                <section className="cs-section">
                    <h2 className="cs-label">Overview</h2>
                    <p className="cs-statement">Manual ID verification is slow, costly, and error-prone.</p>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">The problem</h3>
                            <ul className="cs-list">
                                <li>Manual ID checks are slow and expensive.</li>
                                <li>KYC / KYB needs high-accuracy extraction to prevent fraud.</li>
                                <li>Reading documents by hand doesn't scale.</li>
                            </ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">The solution</h3>
                            <ul className="cs-list">
                                <li>An automated API that reads any ID or passport.</li>
                                <li>Extracts the portrait, key fields, and MRZ in one call.</li>
                                <li>Containerized so it runs the same anywhere.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Architecture */}
                <section className="cs-section">
                    <h2 className="cs-label">How it works</h2>
                    <p className="cs-statement">One upload in, structured JSON out.</p>
                    <div className="cs-flow">
                        {["Upload ID image", "FastAPI · app.py", "OpenCV + EasyOCR", "JSON result"].map((s, i, a) => (
                            <span className="cs-flow-item" key={s}>{s}{i < a.length - 1 && <span className="cs-flow-arrow">→</span>}</span>
                        ))}
                    </div>
                    <p className="cs-body">
                        A <code>FastAPI</code> service routes each request through the right pipeline — face detection for portraits, OCR + regex for fields, and a dedicated parser for the MRZ — and returns a clean structured response.
                    </p>
                </section>

                {/* Endpoints */}
                <section className="cs-section">
                    <h2 className="cs-label">Three endpoints</h2>
                    <div className="cs-featureGrid">
                        {ENDPOINTS.map((e) => (
                            <div className="cs-feature" key={e.path}>
                                <h4 className="cs-item-head"><code>{e.path}</code></h4>
                                <p className="cs-item-body">{e.b}</p>
                                <ul className="cs-list" style={{ marginTop: '14px' }}>
                                    {e.steps.map((s) => <li key={s}>{s}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Portrait extraction */}
                <section className="cs-section">
                    <h2 className="cs-label">Portrait extraction</h2>
                    <p className="cs-statement">Finding the face with a Haar cascade.</p>
                    <div className="cs-flow">
                        {["Grayscale", "detectMultiScale()", "Largest face", "Crop", "Base64"].map((s, i, a) => (
                            <span className="cs-flow-item" key={s}>{s}{i < a.length - 1 && <span className="cs-flow-arrow">→</span>}</span>
                        ))}
                    </div>
                    <p className="cs-body">
                        OpenCV's pre-trained Haar cascade detects faces from intensity-based features. I tuned <code>scaleFactor = 1.1</code> (scan at multiple sizes) and <code>minNeighbors = 5</code> (confirm a face and cut false positives), then keep the largest detection and crop it out.
                    </p>
                    <div className="cs-figure"><div className="cs-ph">Portrait crop — sample (coming)</div></div>
                </section>

                {/* MRZ */}
                <section className="cs-section">
                    <h2 className="cs-label">MRZ parsing</h2>
                    <p className="cs-statement">Turning the machine-readable zone into clean fields.</p>
                    <p className="cs-body">
                        The MRZ is the standardized block at the bottom of travel documents, using <code>&lt;</code> as its separator and filler. The parser locates the MRZ lines and reads fields by their fixed character positions — document type and issuing country, name, passport number, nationality, date of birth, sex, and expiry, each followed by a check digit.
                    </p>
                    <p className="cs-body">
                        Today it handles the <strong>2-line passport format (TD3)</strong>. The <strong>3-line ID-card format (TD1)</strong> is next on the roadmap.
                    </p>
                </section>

                {/* Stack */}
                <section className="cs-section">
                    <h2 className="cs-label">Tech stack</h2>
                    <div className="cs-stackGroups">
                        {STACK.map((g) => (
                            <div className="cs-stackGroup" key={g.title}>
                                <h4 className="cs-stackTitle">{g.title}</h4>
                                <div className="cs-chips">{g.items.map((it) => <span className="cs-chip" key={it}>{it}</span>)}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Delivered + roadmap */}
                <section className="cs-section">
                    <h2 className="cs-label">What I built &amp; what's next</h2>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">Delivered</h3>
                            <ul className="cs-list">
                                <li>A FastAPI service with three extraction endpoints.</li>
                                <li>Portrait detection + crop returned as base64.</li>
                                <li>Field extraction (Name, DOB, Expiry) via OCR + regex.</li>
                                <li>Passport MRZ (TD3) parsing into structured fields.</li>
                                <li>Containerized and published to Docker Hub.</li>
                            </ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">Roadmap</h3>
                            <ul className="cs-list">
                                <li>3-line ID-card MRZ (TD1) support.</li>
                                <li>Stronger Arabic OCR — moving to Surya.</li>
                                <li>Cloud deployment (Render) with a public URL.</li>
                                <li>A polished front-end for the API.</li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </>
    )
}
